import { PERMISSIONS_MAP, PERMISSIONS_BY_ROLES } from '../constants';

/**
 * Service class for handling permission checks and lookups in the Ezlogic system.
 *
 * Provides static methods to check if a user role has a particular permission,
 * check multiple permissions at once, and retrieve all permissions available to a given role.
 *
 * @example
 * import { PermissionService } from './PermissionService';
 *
 * // Check if an administrator can manage users
 * const canManageUsers = PermissionService.hasPermission(PERMISSION_ROLES.ACCOUNT_ADMINISTRATOR, PERMISSIONS.MANAGE_USERS);
 *
 * // Check multiple permissions for a user
 * const [canManageUsers, canManageMeshbots] = PermissionService.hasPermissions(PERMISSION_ROLES.ADVANCED_USER, [PERMISSIONS.MANAGE_USERS, PERMISSIONS.MANAGE_MESHBOTS]);
 *
 * // Get all permissions for a role
 * const adminPermissions = PermissionService.getPermissionsByRole(PERMISSION_ROLES.ACCOUNT_ADMINISTRATOR);
 */
export class PermissionService {
    /**
     * Checks if a given role has the specified permission.
     *
     * @param {number} role - The user role code (from {@link PERMISSION_ROLES}).
     * @param {string} key - The permission key (from {@link PERMISSIONS}).
     * @returns {boolean} True if the role includes the permission, false otherwise.
     */
    static hasPermission(role, key) {
        const allowed = PERMISSIONS_MAP[key];

        return allowed ? allowed.includes(role) : false;
    }

    /**
     * Checks if a given role has each of the specified permissions.
     *
     * @param {number} role - The user role code.
     * @param {string[]} keys - Array of permission keys to check.
     * @returns {boolean[]} Array of boolean values indicating if the role has each permission.
     *
     * @example
     * const result = PermissionService.hasPermissions(9, ['MANAGE_USERS', 'MANAGE_MESHBOTS']);
     * // result: [true, false]
     */
    static hasPermissions(role, keys = []) {
        return keys.map((key) => PermissionService.hasPermission(role, key));
    }

    /**
     * Returns the list of permission keys available to the given role.
     *
     * @param {number} role - The user role code.
     * @returns {string[]|undefined} Array of permission keys, or undefined if the role is not found.
     *
     * @example
     * const perms = PermissionService.getPermissionsByRole(10);
     * // perms: ['MANAGE_USERS', ...]
     */
    static getPermissionsByRole(role) {
        return PERMISSIONS_BY_ROLES[role];
    }
}
